import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageSquare, Trash2, FolderOpen, Check, Tag, Star, Pin } from 'lucide-react';
import type { Conversation } from '@/types/api';
import type { Project } from '@/services/projectService';

interface ConversationListItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
  onDelete: () => void;
  formatTime: (iso: string) => string;
  isZh: boolean;
  projectName?: string;
  projectColor?: string;
  projects?: Project[];
  onAssignProject?: (conversationId: string, projectId: string | null) => void;
  starred?: boolean;
  pinned?: boolean;
  onToggleStar?: (id: string) => void;
  onTogglePin?: (id: string) => void;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}

export const ConversationListItem = React.memo(function ConversationListItem({
  conversation, isActive, onClick, onDelete, formatTime, isZh,
  projectName, projectColor, projects = [], onAssignProject,
  starred, pinned, onToggleStar, onTogglePin,
  selectionMode, selected, onToggleSelect,
}: ConversationListItemProps) {
  const [showDelete, setShowDelete] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [showProjectMenu, setShowProjectMenu] = React.useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      className="relative group px-1"
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => { setShowDelete(false); setConfirmDelete(false); setShowProjectMenu(false); }}
    >
      <button
        onClick={selectionMode ? () => onToggleSelect?.(conversation.id) : onClick}
        className={`w-full flex items-start gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${
          selectionMode && selected
            ? 'bg-celestial-saturn/15 border border-celestial-saturn/40'
            : isActive
              ? 'bg-celestial-saturn/10 border border-celestial-saturn/20'
              : 'hover:bg-white/[0.04] border border-transparent'
        }`}
      >
        {selectionMode ? (
          <span className={`mt-0.5 w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center transition-colors ${
            selected ? 'bg-celestial-saturn border-celestial-saturn' : 'border-white/25'
          }`}>
            {selected && <Check size={11} className="text-black" />}
          </span>
        ) : (
          <MessageSquare
            size={14}
            className={`mt-0.5 flex-shrink-0 ${
              isActive ? 'text-celestial-saturn' : 'text-white/25 group-hover:text-white/45'
            } transition-colors`}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-medium truncate flex items-center gap-1 ${
            isActive ? 'text-celestial-saturn' : 'text-white/60 group-hover:text-white/80'
          }`}>
            {pinned && <Pin size={10} className="text-celestial-saturn flex-shrink-0" />}
            <span className="truncate">{conversation.title || (isZh ? '未命名对话' : 'Untitled')}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-white/25">
              {conversation.messageCount} {(isZh ? '条消息' : 'msgs')}
            </span>
            <span className="text-[10px] text-white/15">·</span>
            <span className="text-[10px] text-white/25">{formatTime(conversation.lastActiveAt)}</span>
            {projectName && (
              <span className="inline-flex items-center gap-1 text-[10px] text-white/40 pl-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: projectColor }} />
                <span className="truncate max-w-[80px]">{projectName}</span>
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className={`flex-shrink-0 flex items-center transition-all duration-200 ${
          showDelete && !selectionMode ? 'opacity-100' : 'opacity-0'
        } ${selectionMode ? 'hidden' : ''}`}>
          {/* Star */}
          <button
            onClick={(e) => { e.stopPropagation(); onToggleStar?.(conversation.id); }}
            className={`p-1 rounded-lg transition-colors ${
              starred ? 'text-yellow-400 hover:bg-white/[0.06]' : 'text-white/20 hover:text-yellow-400 hover:bg-white/[0.06]'
            }`}
            title={isZh ? '收藏' : 'Star'}
          >
            <Star size={12} fill={starred ? 'currentColor' : 'none'} />
          </button>
          {/* Pin */}
          <button
            onClick={(e) => { e.stopPropagation(); onTogglePin?.(conversation.id); }}
            className={`p-1 rounded-lg transition-colors ${
              pinned ? 'text-celestial-saturn hover:bg-white/[0.06]' : 'text-white/20 hover:text-celestial-saturn hover:bg-white/[0.06]'
            }`}
            title={isZh ? '置顶' : 'Pin'}
          >
            <Pin size={12} />
          </button>
          {/* Assign to project */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowProjectMenu((v) => !v); }}
              className="p-1 rounded-lg text-white/20 hover:text-celestial-saturn hover:bg-white/[0.06] transition-colors"
              title={isZh ? '归入项目' : 'Assign to project'}
            >
              <Tag size={12} />
            </button>
            <AnimatePresence>
              {showProjectMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.97 }}
                  transition={{ duration: 0.15 }}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-0 top-7 z-30 w-44 max-h-60 overflow-y-auto custom-scrollbar bg-[#0b1020] border border-white/10 rounded-xl shadow-xl p-1"
                >
                  <button
                    onClick={() => { onAssignProject?.(conversation.id, null); setShowProjectMenu(false); }}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-white/50 hover:bg-white/[0.06]"
                  >
                    <FolderOpen size={12} /> {isZh ? '无项目' : 'No project'}
                    {!conversation.projectId && <Check size={12} className="ml-auto text-celestial-saturn" />}
                  </button>
                  {projects.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { onAssignProject?.(conversation.id, p.id); setShowProjectMenu(false); }}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-white/70 hover:bg-white/[0.06]"
                    >
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                      <span className="truncate">{p.name}</span>
                      {conversation.projectId === p.id && <Check size={12} className="ml-auto text-celestial-saturn" />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Delete */}
          {confirmDelete ? (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              title={isZh ? '确认删除' : 'Confirm delete'}
            >
              <Trash2 size={12} />
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
              className="p-1 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              title={isZh ? '删除对话' : 'Delete conversation'}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </button>
    </motion.div>
  );
});
